import { useState } from 'react';
import {
  Panel,
  ProgressBar,
  ShopProductCard,
  Stat,
  StatGrid,
  ViewHeader,
  clamp01,
  formatExact,
  type BadgeAccent,
} from '@spacegame/ui';
import {
  CHRONOS_DAILY_CAP,
  SHOP_CATEGORY_PRODUCTS,
  chronosBonusPercent,
  productGrant,
  remainingDailyChronos,
  type ShopCategory,
  type ShopProduct,
} from '@spacegame/engine';
import { useGame, useGameActions } from '../game/GameProvider';
import { shopProductArt } from '../components/ShopProductArt';
import { ShopCheckoutModal } from '../components/ShopCheckoutModal';

const CATEGORY_META: Record<ShopCategory, { title: string; eyebrow: string }> = {
  chronos: { title: 'Chronos-Kristalle', eyebrow: 'Premium-Währung · Warp-Booster, Bauzeit, Kosmetik' },
  resources: { title: 'Rohstoff-Pakete', eyebrow: 'Sofort geliefert · Menge richtet sich nach deiner Kolonie' },
  bundle: { title: 'Bundles & Angebote', eyebrow: 'Kristalle + Rohstoffe im Paket' },
};

const BADGE_ACCENT: Record<string, BadgeAccent> = {
  'Bester Wert': 'success',
  Beliebt: 'silicate',
  'Maximaler Bonus': 'chronos',
  'Einmaliges Angebot': 'warning',
};

function formatEur(amount: number): string {
  return amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export function ShopView() {
  const { game, clock } = useGame();
  const actions = useGameActions();
  const [checkoutProduct, setCheckoutProduct] = useState<ShopProduct | null>(null);
  const [checkoutStatus, setCheckoutStatus] = useState<'confirm' | 'success'>('confirm');
  const [purchasing, setPurchasing] = useState(false);

  const now = clock();
  const remainingChronos = remainingDailyChronos(game, now);
  const spentToday = CHRONOS_DAILY_CAP - remainingChronos;

  const closeCheckout = () => {
    setCheckoutProduct(null);
    setCheckoutStatus('confirm');
  };

  const confirmPurchase = async () => {
    if (!checkoutProduct || purchasing) return;
    setPurchasing(true);
    try {
      const result = await actions.purchaseProduct(checkoutProduct.id);
      if (!result) {
        // Rejected (daily cap hit concurrently, already redeemed, ...) — the
        // global error toast already explains why (see GameProvider/Toasts).
        closeCheckout();
        return;
      }
      setCheckoutStatus('success');
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <>
      <ViewHeader
        eyebrow="Chronos-Shop · Demo-Zahlung, kein echtes Geld"
        title="Ausrüstungslager"
        description="Kristalle, Rohstoffe und Bundles — sofort gutgeschrieben. Kosmetische Vorteile und Zeitersparnis, nie Kampfstärke, die man sich nicht auch erspielen könnte."
      />

      <StatGrid columns={2}>
        <Stat
          label="Chronos-Kristalle"
          value={formatExact(game.resources.chronos)}
          accent="chronos"
          hint="Dein aktueller Bestand"
        />
        <Stat
          label="Heute kaufbar"
          value={formatExact(remainingChronos)}
          accent={remainingChronos > 0 ? 'success' : 'danger'}
          hint={`Tageslimit ${formatExact(CHRONOS_DAILY_CAP)} Kristalle`}
        />
      </StatGrid>

      <Panel eyebrow="Fair-Play-Grenze" title="Tages-Kauflimit">
        <ProgressBar
          value={clamp01(spentToday / CHRONOS_DAILY_CAP)}
          accent={remainingChronos > 0 ? 'chronos' : 'danger'}
          hint={`${formatExact(spentToday)} / ${formatExact(CHRONOS_DAILY_CAP)} Kristalle heute gekauft`}
          aria-label="Tages-Kauflimit Chronos-Kristalle"
        />
        <p className="mt-2 text-xs leading-relaxed text-content-muted">
          Die käufliche Menge an Chronos-Kristallen ist pro Tag begrenzt — Kampfvorteile bleiben
          dadurch immer auch ingame erspielbar, nie nur kaufbar.
        </p>
      </Panel>

      {(Object.keys(SHOP_CATEGORY_PRODUCTS) as ShopCategory[]).map((category) => {
        const meta = CATEGORY_META[category];
        return (
          <Panel key={category} title={meta.title} eyebrow={meta.eyebrow} corners>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {SHOP_CATEGORY_PRODUCTS[category].map((product) => {
                const alreadyRedeemed = product.oneTime && game.shop.redeemedOnce.includes(product.id);
                const overCap = (product.chronos ?? 0) > remainingChronos;
                const bonus = product.category === 'chronos' ? chronosBonusPercent(product) : null;

                return (
                  <ShopProductCard
                    key={product.id}
                    label={product.label}
                    description={product.description}
                    priceLabel={formatEur(product.priceEur)}
                    badge={product.badge}
                    badgeAccent={product.badge ? BADGE_ACCENT[product.badge] : undefined}
                    bonusLabel={bonus && bonus > 0 ? `+${bonus} % mehr Kristalle pro Euro` : undefined}
                    art={shopProductArt(product.art, product.label)}
                    featured={product.badge === 'Bester Wert' || product.badge === 'Maximaler Bonus'}
                    disabled={alreadyRedeemed || overCap}
                    disabledReason={
                      alreadyRedeemed ? 'Bereits eingelöst' : overCap ? 'Tageslimit erreicht' : undefined
                    }
                    onBuy={() => {
                      setCheckoutProduct(product);
                      setCheckoutStatus('confirm');
                    }}
                  />
                );
              })}
            </div>
          </Panel>
        );
      })}

      <ShopCheckoutModal
        open={checkoutProduct !== null}
        status={checkoutStatus}
        productLabel={checkoutProduct?.label ?? ''}
        priceLabel={checkoutProduct ? formatEur(checkoutProduct.priceEur) : ''}
        art={checkoutProduct ? shopProductArt(checkoutProduct.art, checkoutProduct.label) : null}
        grant={checkoutProduct ? productGrant(game, now, checkoutProduct) : {}}
        onConfirm={confirmPurchase}
        onClose={closeCheckout}
      />
    </>
  );
}
